// src/views/operateur/OperateurFait.jsx — LOT 2 OPÉRATEUR (pièces F + G,
// ADR-304) : la bascule « Ce qu'il a fait » — ses gestes et ses habitudes.
//
// SES GESTES (pièce G) — la timeline : les actes de `operateur_actes` + les
// décisions DÉRIVÉES de `operateur_propositions` (envoyée = point VERT
// « validé », avec l'heure) — jamais de double écriture. Point BLEU = seul.
// « ↩ Défaire » sur les defaisable SEULS (sinon la raison, en italique).
// « Là où il s'est trompé » = dérivé des refus « faux » + la règle mémoire née.
// Le réglage « la prochaine fois » (demande / propose / fait seul) n'existe
// QUE sur les signalements d'habitude (ACTES_FAIT_SEUL_ELIGIBLES) — les
// cartes de réponse voyageur gardent « il proposera toujours » (verdict V10).
//
// SES HABITUDES (pièce F) — habitude bloquée (ambre), bloc « il propose »
// (D'accord / Non merci), la liste (déclencheur / dernier passage / bilan /
// interrupteur), micro « Lui confier une habitude » HONNÊTE pas-encore, et la
// carte « Sa limite du jour » (barre ambre au-delà de 80 %).
//
// Textes du proto AU MOT PRÈS (docs/handoff/admin-proto.html, opFait/opPlans).

import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../../supabase'
import { accord } from '../../lib/accord-nombres'

async function postAction(body) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Session expirée')
  const res = await fetch('/api/beds24-mapping', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) { const e = new Error(json?.message || json?.error || `Erreur ${res.status}`); e.code = json?.error; throw e }
  return json
}

const microIc = (t) => (
  <svg width={t} height={t} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="2" width="6" height="11" rx="3" />
    <path d="M5 10v1a7 7 0 0 0 14 0v-1" /><path d="M12 18v4" />
  </svg>
)

// Le type d'acte éligible au réglage d'autonomie — MIROIR de l'allow-list
// serveur ACTES_FAIT_SEUL_ELIGIBLES (lib/operateur-handlers.js, sentinelle
// operateur-fait-seul-sentinelle.test.js). Jamais d'envoi voyageur ni d'argent.
const TYPES_REGLABLES = ['signalement']

function heureCourt(iso) {
  const d = iso ? new Date(iso) : null
  if (!d || Number.isNaN(d.getTime())) return ''
  const [h, m] = new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Paris',
  }).format(d).split(':')
  return m === '00' ? `${Number(h)}h` : `${Number(h)}h${m}`
}

function jourParis(iso) {
  const d = iso ? new Date(iso) : null
  if (!d || Number.isNaN(d.getTime())) return null
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(d)
}

export default function OperateurFait({ nomLogement }) {
  const [vue, setVue] = useState('gestes')
  const [fait, setFait] = useState({ ok: null, gestes: [], rates: [], autonomie: {}, limite: null })
  const [plans, setPlans] = useState({ ok: null, actif: true, plans: [], limite: null })
  const [microNote, setMicroNote] = useState(false)
  const [erreur, setErreur] = useState(null)
  const vivantRef = useRef(true)
  useEffect(() => { vivantRef.current = true; return () => { vivantRef.current = false } }, [])

  const charger = useCallback(async () => {
    try {
      const f = await postAction({ action: 'operateur_fait_list' })
      if (vivantRef.current) setFait({ ok: true, gestes: f.gestes || [], rates: f.rates || [], autonomie: f.autonomie || {}, limite: f.limite || null })
    } catch (e) {
      console.error('[operateur] fait', e?.message || e)
      if (vivantRef.current) setFait((p) => ({ ...p, ok: false }))
    }
    try {
      const p = await postAction({ action: 'operateur_plans_list' })
      if (vivantRef.current) setPlans({ ok: true, actif: p.actif !== false, plans: p.plans || [], limite: p.limite || null })
    } catch (e) {
      console.error('[operateur] plans', e?.message || e)
      if (vivantRef.current) setPlans((p) => ({ ...p, ok: false }))
    }
  }, [])
  useEffect(() => { const id = setTimeout(charger, 0); return () => clearTimeout(id) }, [charger])

  const decidePlan = useCallback(async (id, decision) => {
    setErreur(null)
    try { await postAction({ action: 'operateur_plan_decide', id, decision }); charger() }
    catch (e) { setErreur(e?.message || 'La décision a échoué.') }
  }, [charger])

  const basculePlan = useCallback(async (plan) => {
    setErreur(null)
    try { await postAction({ action: 'operateur_plan_toggle', id: plan.id, on: plan.statut !== 'active' }); charger() }
    catch (e) { setErreur(e?.message || "L'interrupteur a échoué.") }
  }, [charger])

  const defaire = useCallback(async (id) => {
    setErreur(null)
    try { await postAction({ action: 'operateur_acte_defaire', id: String(id) }); charger() }
    catch (e) { setErreur(e?.message || 'Le geste n\'a pas pu être défait.') }
  }, [charger])

  const reglerAutonomie = useCallback(async (typeActe, niveau) => {
    setErreur(null)
    try {
      await postAction({ action: 'operateur_autonomie_set', type_acte: typeActe, niveau, portee_type: 'partout' })
      charger()
    } catch (e) { setErreur(e?.message || 'Le réglage a échoué.') }
  }, [charger])

  const actifs = plans.plans.filter((p) => p.statut === 'active')
  const proposes = plans.plans.filter((p) => p.statut === 'proposee')
  const coinces = actifs.filter((p) => p.etat === 'coince')
  const limite = plans.limite || fait.limite
  const gestesAuj = fait.gestes.filter((g) => jourParis(g.created_at) === jourParis(new Date().toISOString()))

  const rendGestes = () => (
    <div data-testid="operateur-gestes">
      {fait.ok === false && <p className="note">La lecture de ses gestes a échoué.</p>}

      {/* « Là où il s'est trompé » — dérivé, jamais écrit deux fois. */}
      {fait.rates.length > 0 && (
        <div data-testid="operateur-rates">
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, margin: '0 0 10px' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#87b8e6', flexShrink: 0 }} />
            <b style={{ flex: 1, fontSize: 11.5, color: 'var(--navy-d)' }}>Là où il s'est trompé</b>
            <span style={{ fontSize: 10.5, color: 'var(--gris-d)' }}>{fait.rates.length}</span>
          </div>
          {fait.rates.map((r) => (
            <div key={r.id} style={{ background: 'var(--ciel-p)', border: '1px solid #c3d5e4',
              borderRadius: 14, padding: '12px 13px', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, marginBottom: 6 }}>
                <span style={{ fontSize: 10, color: 'var(--navy)', opacity: .7, width: 60, flexShrink: 0 }}>
                  {heureCourt(r.created_at)}
                </span>
                <b style={{ flex: 1, fontSize: 12.5, color: 'var(--navy-d)', lineHeight: 1.4 }}>{r.q}</b>
              </div>
              <p style={{ fontSize: 11, color: 'var(--navy)', margin: 0, lineHeight: 1.55 }}>
                <b>Pourquoi</b> · {r.pq}
              </p>
              {r.appris && (
                <p style={{ fontSize: 11, color: 'var(--vert-tx)', margin: '6px 0 0', lineHeight: 1.55 }}>
                  ✓ {r.appris}
                </p>
              )}
            </div>
          ))}
          <div style={{ height: 14 }} />
        </div>
      )}

      {fait.ok === true && fait.gestes.length === 0 && (
        <p className="note" data-testid="operateur-gestes-vide">Aucun geste ces sept derniers jours.</p>
      )}

      {fait.gestes.map((g) => {
        const seul = g.seul === true
        return (
          <div key={g.id} data-testid={`geste-${g.id}`} style={{ display: 'flex', gap: 11, marginBottom: 14 }}>
            <span style={{ width: 44, flexShrink: 0, textAlign: 'right', paddingTop: 1 }}>
              <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--navy-d)' }}>
                {heureCourt(g.created_at)}
              </span>
              <span style={{ display: 'block', fontSize: 9, color: seul ? '#87b8e6' : '#2f9e6e', marginTop: 2 }}>
                {seul ? 'seul' : 'validé'}
              </span>
            </span>
            <span style={{ flexShrink: 0, width: 9, display: 'flex', flexDirection: 'column',
              alignItems: 'center', paddingTop: 4 }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%',
                background: seul ? '#87b8e6' : '#2f9e6e', flexShrink: 0 }} />
              <span style={{ flex: 1, width: 1.5, background: 'var(--bord)', marginTop: 4 }} />
            </span>
            <span style={{ flex: 1, minWidth: 0, paddingBottom: 2 }}>
              <span style={{ display: 'block', fontSize: 12.5, color: 'var(--navy-d)', lineHeight: 1.45 }}>
                {g.tx}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                {g.ou_ecran && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, height: 24,
                    padding: '0 9px', borderRadius: 12, background: 'var(--fond)',
                    border: '1px solid var(--bord)', fontSize: 10, color: 'var(--gris)' }}>
                    {g.ou_ecran} <span style={{ color: 'var(--gris-d)' }}>›</span>
                  </span>
                )}
                {g.quoi && (
                  <span style={{ fontSize: 10, color: 'var(--gris-d)' }}>
                    {g.type_acte === 'reponse_envoyee' && nomLogement ? nomLogement(g.quoi) : g.quoi}
                  </span>
                )}
                {g.defaisable && !g.defait_at ? (
                  <button type="button" data-testid={`geste-${g.id}-defaire`} onClick={() => defaire(g.id)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 24,
                      padding: '0 9px', borderRadius: 12, border: '1px solid var(--terra-bd)',
                      background: 'var(--terra-bg)', fontSize: 10, color: 'var(--terra-tx)',
                      marginLeft: 'auto', cursor: 'pointer', fontFamily: 'inherit' }}>↩ Défaire</button>
                ) : (
                  <span style={{ fontSize: 10, color: 'var(--gris-d)', marginLeft: 'auto', fontStyle: 'italic' }}>
                    {g.defait_at ? 'défait' : g.pq_pas || (g.pq === 'pour information' ? "rien n'a été changé" : 'rien à défaire')}
                  </span>
                )}
              </span>
              {/* Le réglage « la prochaine fois » — signalements d'habitude SEULS
                  (V10 : les cartes de réponse voyageur gardent « il proposera
                  toujours », ce segment n'y existe pas). */}
              {TYPES_REGLABLES.includes(g.type_acte) && (
                <span data-testid={`geste-${g.id}-prochaine-fois`}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <span style={{ fontSize: 9.5, color: 'var(--gris)' }}>La prochaine fois</span>
                  <span style={{ display: 'inline-flex', gap: 2, background: 'var(--fond)',
                    border: '1px solid var(--bord)', borderRadius: 10, padding: 2 }}>
                    {[['demande', 'demande'], ['propose', 'propose'], ['fait_seul', 'fait seul']].map(([val, lb]) => {
                      const courant = (fait.autonomie[g.type_acte] || 'demande') === val
                      return (
                        <button key={val} type="button"
                          data-testid={`geste-${g.id}-niveau-${val}`}
                          onClick={() => reglerAutonomie(g.type_acte, val)}
                          style={{ fontSize: 9.5, padding: '3px 8px', borderRadius: 8, border: 'none',
                            cursor: 'pointer', fontFamily: 'inherit', fontWeight: courant ? 700 : 400,
                            background: courant ? '#fff' : 'transparent',
                            color: courant ? 'var(--navy-d)' : 'var(--gris)' }}>{lb}</button>
                      )
                    })}
                  </span>
                </span>
              )}
            </span>
          </div>
        )
      })}

      <p className="note">
        Le point bleu, il l'a fait seul — le vert, vous aviez validé. Seuls les
        gestes réversibles portent « Défaire ».
      </p>
    </div>
  )

  const rendHabitudes = () => (
    <div data-testid="operateur-habitudes">
      {plans.ok === true && !plans.actif && (
        <p className="note" data-testid="habitudes-inerte">
          Ses habitudes ne sont pas encore en service : leur mise en base n'est pas appliquée.
        </p>
      )}
      {plans.ok === false && <p className="note">La lecture de ses habitudes a échoué.</p>}

      {/* L'habitude bloquée — l'ambre d'abord (proto). */}
      {coinces.length > 0 && (
        <div data-testid="habitudes-coince" style={{ background: 'var(--ambre-bg)',
          border: '1px solid var(--ambre-bd)', borderRadius: 16, padding: 13, marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--ambre)', flexShrink: 0 }} />
            <b style={{ flex: 1, fontSize: 12, color: 'var(--ambre-tx)' }}>
              {coinces.length > 1 ? 'Des habitudes sont bloquées' : 'Une habitude est bloquée'}
            </b>
          </div>
          {coinces.map((p) => (
            <div key={p.id}>
              <b style={{ display: 'block', fontSize: 12.5, color: 'var(--ambre-tx)', lineHeight: 1.4 }}>{p.quoi}</b>
              {p.souci && (
                <p style={{ fontSize: 11, color: 'var(--ambre-tx)', margin: '7px 0 0', lineHeight: 1.55 }}>{p.souci}</p>
              )}
              {p.quoi_faire && (
                <p style={{ fontSize: 11, color: 'var(--ambre-tx)', margin: '5px 0 0', lineHeight: 1.55 }}>
                  <b>{p.quoi_faire}</b>
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Ce qu'il propose de prendre en charge. */}
      {proposes.length > 0 && (
        <div data-testid="habitudes-propose" style={{ background: 'var(--ciel-p)',
          border: '1px solid #c3d5e4', borderRadius: 16, padding: 13, marginBottom: 18 }}>
          <span className="tag t-b" style={{ marginBottom: 8 }}>il propose</span>
          {proposes.map((p) => (
            <div key={p.id}>
              <b style={{ display: 'block', fontSize: 12.5, lineHeight: 1.4 }}>
                {p.titre_declencheur} — {p.quoi ? p.quoi.charAt(0).toLowerCase() + p.quoi.slice(1) : ''}
              </b>
              {p.propose_pq && (
                <p style={{ fontSize: 11, color: 'var(--navy)', margin: '6px 0 0', lineHeight: 1.55 }}>
                  Parce que {p.propose_pq}.
                </p>
              )}
              <div className="act" style={{ marginTop: 10 }}>
                <button type="button" className="bt bt-p" data-testid={`plan-${p.id}-daccord`}
                  onClick={() => decidePlan(p.id, 'daccord')}>D'accord</button>
                <button type="button" className="bt bt-s" data-testid={`plan-${p.id}-non-merci`}
                  onClick={() => decidePlan(p.id, 'non_merci')}>Non merci</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="inter" style={{ marginTop: 0 }}>Ce qu'il fait sans qu'on le lui redemande</p>
      {plans.ok === true && plans.actif && plans.plans.filter((p) => ['active', 'eteinte'].includes(p.statut)).length === 0 && (
        <p className="note" data-testid="habitudes-vide">Aucune habitude pour l'instant.</p>
      )}
      {plans.plans.filter((p) => ['active', 'eteinte'].includes(p.statut)).map((p) => {
        const on = p.statut === 'active'
        const ton = { ok: ['#2f9e6e', 'var(--vert-tx)'], tiede: ['#d4a24c', 'var(--ambre-tx)'], coince: ['#d4a24c', 'var(--ambre-tx)'] }[p.etat] || ['#c3cdd6', 'var(--gris)']
        return (
          <div key={p.id} className="carte" data-testid={`plan-${p.id}`}
            style={{ margin: '0 0 8px', ...(on ? {} : { opacity: .55 }) }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, marginTop: 5,
                background: on ? ton[0] : '#c3cdd6' }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 10.5, color: 'var(--gris)' }}>{p.titre_declencheur}</span>
                <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, marginTop: 2, lineHeight: 1.45 }}>{p.quoi}</span>
                <span style={{ display: 'block', fontSize: 10.5, color: 'var(--gris-d)', marginTop: 5 }}>
                  {on ? (p.dernier_resume || 'pas encore passée') : 'éteinte'}
                </span>
                {on && p.etat === 'coince' && (
                  <span style={{ display: 'block', fontSize: 10.5, marginTop: 3, color: 'var(--ambre-tx)' }}>
                    bloquée — voir plus haut
                  </span>
                )}
              </span>
              <button type="button" data-testid={`plan-${p.id}-interrupteur`} onClick={() => basculePlan(p)}
                aria-label={on ? 'Éteindre cette habitude' : 'Rallumer cette habitude'}
                style={{ width: 40, height: 24, borderRadius: 12, flexShrink: 0, position: 'relative',
                  border: 'none', cursor: 'pointer', background: on ? '#2f9e6e' : '#e0e5ea' }}>
                <span style={{ position: 'absolute', top: 3, ...(on ? { right: 3 } : { left: 3 }),
                  width: 18, height: 18, borderRadius: '50%', background: '#fff' }} />
              </button>
            </div>
          </div>
        )
      })}

      {/* Micro HONNÊTE — « pas encore ». */}
      <button type="button" data-testid="habitudes-micro" onClick={() => setMicroNote((v) => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
          background: '#fff', border: '1px dashed #c3d5e4', borderRadius: 12, padding: 12,
          minHeight: 52, marginTop: 4, cursor: 'pointer', fontFamily: 'inherit' }}>
        <span style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--ciel-p)',
          color: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0 }}>{microIc(13)}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--navy-d)' }}>
            Lui confier une habitude
          </span>
          <span style={{ display: 'block', fontSize: 10.5, color: 'var(--gris)', marginTop: 1 }}>
            « chaque vendredi, dis-moi qui n'a pas payé »
          </span>
        </span>
      </button>
      {microNote && (
        <p className="note" data-testid="habitudes-micro-note" style={{ marginTop: 8 }}>
          Pas encore — lui parler arrivera avec l'opérateur complet.
        </p>
      )}

      {/* Sa limite du jour — la carte du proto, barre ambre > 80 %. */}
      {limite && limite.fait !== null && (
        <div className="carte" data-testid="operateur-limite" style={{ margin: '16px 0 0', background: 'var(--fond)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 11.5, fontWeight: 600 }}>Sa limite du jour</span>
              <span style={{ display: 'block', fontSize: 10.5, color: 'var(--gris)', marginTop: 2 }}>
                {accord(limite.fait, 'action')} sur {limite.max} — il s'arrête et vous prévient avant d'aller plus loin
              </span>
            </span>
            <span style={{ width: 44, flexShrink: 0, textAlign: 'right', fontSize: 13, fontWeight: 600,
              color: limite.fait / limite.max > 0.8 ? 'var(--ambre-tx)' : 'var(--navy-d)' }}>
              {Math.round((limite.fait / limite.max) * 100)} %
            </span>
          </div>
          <span style={{ display: 'block', height: 4, borderRadius: 2, background: '#eef2f6',
            overflow: 'hidden', marginTop: 9 }}>
            <span data-testid="operateur-limite-barre" style={{ display: 'block', height: '100%', borderRadius: 2,
              width: `${Math.min(100, (limite.fait / limite.max) * 100)}%`,
              background: limite.fait / limite.max > 0.8 ? '#d4a24c' : '#87b8e6' }} />
          </span>
        </div>
      )}
    </div>
  )

  return (
    <div data-testid="operateur-fait">
      {erreur && (
        <div data-testid="fait-erreur" style={{ margin: '0 0 10px', padding: '9px 12px',
          background: 'var(--terra-bg)', border: '1px solid var(--terra-bd)', borderRadius: 12,
          fontSize: 11, color: 'var(--terra-tx)' }}>{erreur}</div>
      )}
      <div className="bascule" style={{ margin: '0 0 14px' }}>
        <button type="button" className={vue === 'gestes' ? 'on' : ''} data-testid="bascule-gestes"
          onClick={() => setVue('gestes')}>
          Ses gestes
          <span style={{ fontSize: 9.5, opacity: .6, marginLeft: 5 }}>{gestesAuj.length}</span>
        </button>
        <button type="button" className={vue === 'habitudes' ? 'on' : ''} data-testid="bascule-habitudes"
          onClick={() => setVue('habitudes')}>
          Ses habitudes
          <span style={{ fontSize: 9.5, opacity: .6, marginLeft: 5 }}>{actifs.length}</span>
        </button>
      </div>
      {vue === 'gestes' ? rendGestes() : rendHabitudes()}
    </div>
  )
}
